import content from '../content';
import { ApiError } from '../utils/api-error';
import { authDebug, getAuthDebugError } from '../utils/auth-debug';
import { textToBase64 } from '../utils/base64-utils';
import { api, apiPublic } from '../utils/data-utils';
import type {
  AdminBulkExportJob,
  AthenaLookupRequest,
  AthenaLookupResponse,
  DataErrorEntry,
  FeedbackEntry,
  FetchFormResponse,
  FunctionMembersResponse,
  FormUsersResponse,
  RewardCosAuditResponse,
  RewardTransferOwnershipCandidatesResponse,
  RewardTransferOwnershipResponse,
  ShareHistoryResponse,
  SqlReportDetail,
  SqlReportJob,
  SqlReportListResponse,
  UpdateFormParams,
  ScsReviewSaveResult,
} from './types';

const parseFileNameFromContentDisposition = (value: string | null): string | undefined => {
  if (!value) return undefined;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(value);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch (_err) {
      return utf8Match[1];
    }
  }
  const simpleMatch = /filename="?([^";]+)"?/i.exec(value);
  return simpleMatch?.[1];
};

export type TableauLicenseKey = {
  email: string;
  key: string;
};

export const getTableauLicenseKeys = async (
  token: string,
  formId?: string,
): Promise<{ licenses: TableauLicenseKey[] }> => {
  const res = await api(token, '/form', {}, {
    tableau_license: 'true',
    ...(formId ? { form_id: formId } : {}),
  });
  const payload = (await res.json()) as { licenses?: unknown };
  if (
    !Array.isArray(payload.licenses) ||
    payload.licenses.some(
      (license) =>
        typeof license !== 'object' ||
        license === null ||
        typeof (license as TableauLicenseKey).email !== 'string' ||
        typeof (license as TableauLicenseKey).key !== 'string',
    )
  ) {
    throw new Error('Invalid Tableau licence response');
  }
  return { licenses: payload.licenses as TableauLicenseKey[] };
};

const isLikelyBase64 = (value: string): boolean => {
  const normalized = value.trim();
  if (!normalized || normalized.length % 4 !== 0) return false;
  return /^[A-Za-z0-9+/=\r\n]+$/.test(normalized);
};

const decodeBase64ToBytes = (base64Value: string): Uint8Array => {
  const binary = atob(base64Value.replace(/\s+/g, ''));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const toDownloadBlob = async (res: Response): Promise<Blob> => {
  const contentType = res.headers.get('content-type') || 'application/octet-stream';
  const normalizedContentType = contentType.toLowerCase();
  const buffer = await res.arrayBuffer();
  const bytes = new Uint8Array(buffer);

  const isZipSignature =
    bytes.length >= 4 &&
    bytes[0] === 0x50 &&
    bytes[1] === 0x4b &&
    bytes[2] === 0x03 &&
    bytes[3] === 0x04;

  const isTextLikeContentType =
    normalizedContentType.startsWith('text/') ||
    normalizedContentType.includes('application/json') ||
    normalizedContentType.includes('+json') ||
    normalizedContentType.includes('application/xml') ||
    normalizedContentType.includes('text/xml');

  if (!isZipSignature && (normalizedContentType.includes('zip') || !isTextLikeContentType)) {
    const textPayload = new TextDecoder().decode(bytes).trim();
    if (isLikelyBase64(textPayload)) {
      const decodedBytes = decodeBase64ToBytes(textPayload);
      return new Blob([decodedBytes], { type: contentType });
    }
  }

  return new Blob([buffer], { type: contentType });
};

type ExportOutputFormat = 'excel' | 'csv' | 'parquet' | 'html' | 'pdf';
type AdminBulkExcelExportType = 'relational' | 'hybrid';

const requestBackgroundExport = async (
  token: string,
  payload: Record<string, unknown>,
): Promise<Response> => {
  const request = (body: Record<string, unknown>) =>
    api(token, '/export', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  const response = await request({ ...payload, background: true });
  if (response.status !== 202) return response;
  const started = (await response.json()) as { job_id?: string };
  if (!started.job_id) throw new Error('Export job ID missing');
  const jobRequest = { report_type: 'download_job', job_id: started.job_id };
  for (let attempt = 0; attempt < 75; attempt += 1) {
    const statusResponse = await request({ ...jobRequest, action: 'status' });
    const job = (await statusResponse.json()) as { status: string; error_message?: string };
    if (job.status === 'completed') return request({ ...jobRequest, action: 'download' });
    if (job.status === 'failed') throw new Error(job.error_message || 'Failed to generate export');
    if (job.status !== 'queued' && job.status !== 'running') {
      throw new Error('Invalid export job status');
    }
    await new Promise<void>((resolve) => window.setTimeout(resolve, 2000));
  }
  throw new Error('Export is still processing. Try again later.');
};

const downloadExportFile = async (
  token: string,
  params: Record<string, string>,
  outputFormat: ExportOutputFormat,
): Promise<{ blob: Blob; filename?: string }> => {
  const res = await requestBackgroundExport(token, { ...params, output_format: outputFormat });

  return {
    blob: await toDownloadBlob(res),
    filename: parseFileNameFromContentDisposition(res.headers.get('content-disposition')),
  };
};

export const downloadAdminBulkForms = async (
  token: string,
  options: {
    outputFormat: ExportOutputFormat;
    reportFilters?: string[];
    departmentFilters?: string[];
    includeAwaitingApproval?: boolean;
    includeInProgress?: boolean;
  },
): Promise<{ blob: Blob; filename?: string }> => {
  const payload: Record<string, unknown> = {
    admin_download_all: true,
    output_format: options.outputFormat,
  };
  if (Array.isArray(options.reportFilters) && options.reportFilters.length > 0) {
    payload.report_filter = options.reportFilters;
  }
  if (Array.isArray(options.departmentFilters) && options.departmentFilters.length > 0) {
    payload.department_filter = options.departmentFilters;
  }
  if (options.includeAwaitingApproval) {
    payload.include_awaiting_approval = true;
  }
  if (options.includeInProgress) {
    payload.include_in_progress = true;
  }

  const res = await requestBackgroundExport(token, payload);

  return {
    blob: await toDownloadBlob(res),
    filename: parseFileNameFromContentDisposition(res.headers.get('content-disposition')),
  };
};

export const startAdminBulkExcelExport = async (
  token: string,
  options: {
    reportFilters?: string[];
    departmentFilters?: string[];
    formIds?: string[];
    includeAwaitingApproval?: boolean;
    includeInProgress?: boolean;
    exportType?: AdminBulkExcelExportType;
  },
): Promise<AdminBulkExportJob> => {
  const payload: Record<string, unknown> = {
    report_type: 'admin_bulk_export',
    action: 'start',
    output_format: 'excel',
    export_type: options.exportType || 'relational',
    relational: true,
  };
  if (Array.isArray(options.reportFilters) && options.reportFilters.length > 0) {
    payload.report_filter = options.reportFilters;
  }
  if (Array.isArray(options.departmentFilters) && options.departmentFilters.length > 0) {
    payload.department_filter = options.departmentFilters;
  }
  if (Array.isArray(options.formIds) && options.formIds.length > 0) {
    payload.form_ids = options.formIds;
  }
  if (options.includeAwaitingApproval) {
    payload.include_awaiting_approval = true;
  }
  if (options.includeInProgress) {
    payload.include_in_progress = true;
  }

  const res = await api(token, '/export', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  return (await res.json()) as AdminBulkExportJob;
};

export const fetchAdminBulkExcelExportStatus = async (
  token: string,
  jobId: string,
): Promise<AdminBulkExportJob> => {
  const res = await api(token, '/export', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      report_type: 'admin_bulk_export',
      action: 'status',
      job_id: jobId,
    }),
  });

  return (await res.json()) as AdminBulkExportJob;
};

export const downloadAdminBulkExcelExport = async (
  token: string,
  jobId: string,
): Promise<{ blob: Blob; filename?: string }> => {
  const res = await api(token, '/export', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      report_type: 'admin_bulk_export',
      action: 'download',
      job_id: jobId,
    }),
  });

  return {
    blob: await toDownloadBlob(res),
    filename: parseFileNameFromContentDisposition(res.headers.get('content-disposition')),
  };
};

export const fetchSqlReports = async (token: string): Promise<SqlReportListResponse> => {
  const res = await api(token, '/export', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      report_type: 'sql_report',
      action: 'list',
    }),
  });

  return (await res.json()) as SqlReportListResponse;
};

export const fetchSqlReport = async (
  token: string,
  reportName: string,
): Promise<SqlReportDetail> => {
  const res = await api(token, '/export', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      report_type: 'sql_report',
      action: 'get',
      report_name: reportName,
    }),
  });

  return (await res.json()) as SqlReportDetail;
};

export const updateSqlReport = async (
  token: string,
  options: { reportName: string; sqlQuery: string; revision: number; validationJobId: string },
): Promise<SqlReportDetail> => {
  const res = await api(token, '/export', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      report_type: 'sql_report',
      action: 'update',
      report_name: options.reportName,
      sql_query: options.sqlQuery,
      revision: options.revision,
      validation_job_id: options.validationJobId,
    }),
  });

  return (await res.json()) as SqlReportDetail;
};

export const validateSqlReport = async (
  token: string,
  options: { reportName: string; formVersion: number; sqlQuery: string },
): Promise<SqlReportJob> => {
  const res = await api(token, '/export', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      report_type: 'sql_report',
      action: 'validate',
      report_name: options.reportName,
      form_version: options.formVersion,
      sql_query: options.sqlQuery,
    }),
  });

  return (await res.json()) as SqlReportJob;
};

export const startSqlReport = async (
  token: string,
  options: { reportName: string; formVersion: number },
): Promise<SqlReportJob> => {
  const res = await api(token, '/export', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      report_type: 'sql_report',
      action: 'start',
      report_name: options.reportName,
      form_version: options.formVersion,
    }),
  });

  return (await res.json()) as SqlReportJob;
};

export const fetchSqlReportStatus = async (token: string, jobId: string): Promise<SqlReportJob> => {
  const res = await api(token, '/export', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      report_type: 'sql_report',
      action: 'status',
      job_id: jobId,
    }),
  });

  return (await res.json()) as SqlReportJob;
};

export const downloadSqlReport = async (
  token: string,
  jobId: string,
): Promise<{ blob: Blob; filename?: string }> => {
  const res = await api(token, '/export', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      report_type: 'sql_report',
      action: 'download',
      job_id: jobId,
    }),
  });

  return {
    blob: await toDownloadBlob(res),
    filename: parseFileNameFromContentDisposition(res.headers.get('content-disposition')),
  };
};

export const convertExportHtmlToPdf = async (
  token: string,
  options: {
    html: string;
    filename?: string;
    formVersion?: number | string;
  },
): Promise<{ blob: Blob; filename?: string }> => {
  return renderExportHtmlPayload(token, options, 'pdf');
};

export const downloadExportRenderedHtml = async (
  token: string,
  options: {
    html: string;
    filename?: string;
    formVersion?: number | string;
  },
): Promise<{ blob: Blob; filename?: string }> => {
  return renderExportHtmlPayload(token, options, 'html');
};

const renderExportHtmlPayload = async (
  token: string,
  options: {
    html: string;
    filename?: string;
    formVersion?: number | string;
  },
  outputFormat: 'html' | 'pdf',
): Promise<{ blob: Blob; filename?: string }> => {
  const payload: Record<string, unknown> = {
    report_type: 'html_to_pdf',
    output_format: outputFormat,
    html_base64: textToBase64(options.html),
  };
  if (typeof options.filename === 'string' && options.filename.trim()) {
    payload.filename = options.filename.trim();
  }
  if (
    (typeof options.formVersion === 'string' && options.formVersion.trim()) ||
    typeof options.formVersion === 'number'
  ) {
    payload.form_version = options.formVersion;
  }

  const res = await requestBackgroundExport(token, payload);

  return {
    blob: await toDownloadBlob(res),
    filename: parseFileNameFromContentDisposition(res.headers.get('content-disposition')),
  };
};

export const downloadAdminProgressStats = async (
  token: string,
  options?: {
    reportFilters?: string[];
    departmentFilters?: string[];
    formIds?: string[];
    requireIdentifier?: boolean;
    reportingQuarter?: string;
  },
): Promise<{ blob: Blob; filename?: string }> => {
  const payload: Record<string, unknown> = {
    report_type: 'progress_stats',
    output_format: 'excel',
  };
  if (Array.isArray(options?.reportFilters) && options?.reportFilters.length > 0) {
    payload.report_filter = options.reportFilters;
  }
  if (Array.isArray(options?.departmentFilters) && options?.departmentFilters.length > 0) {
    payload.department_filter = options.departmentFilters;
  }
  if (Array.isArray(options?.formIds) && options.formIds.length > 0) {
    payload.form_ids = options.formIds;
  }
  if (options?.requireIdentifier === true) {
    payload.require_identifier = true;
  }
  if (typeof options?.reportingQuarter === 'string' && options.reportingQuarter.trim()) {
    payload.reporting_quarter = options.reportingQuarter.trim();
  }

  const res = await requestBackgroundExport(token, payload);

  return {
    blob: await toDownloadBlob(res),
    filename: parseFileNameFromContentDisposition(res.headers.get('content-disposition')),
  };
};

export const fetchAdminProgressStatsRows = async (
  token: string,
  options?: { reportFilters?: string[]; departmentFilters?: string[]; formIds?: string[] },
): Promise<Array<Record<string, unknown>>> => {
  const payload: Record<string, unknown> = {
    report_type: 'progress_stats',
    output_format: 'json',
  };
  if (options?.formIds?.length) payload.form_ids = options.formIds;
  if (Array.isArray(options?.reportFilters) && options.reportFilters.length > 0) {
    payload.report_filter = options.reportFilters;
  }
  if (Array.isArray(options?.departmentFilters) && options.departmentFilters.length > 0) {
    payload.department_filter = options.departmentFilters;
  }

  const res = await requestBackgroundExport(token, payload);

  const json = await res.json();
  const parsed = typeof json?.body === 'string' ? JSON.parse(json.body) : json;
  const rows = parsed?.rows;
  return Array.isArray(rows) ? rows : [];
};

export const prepopulateForm = async (
  token: string,
  params: { pin: string; 'lookup-question': string; 'lookup-value': string },
): Promise<{ matched: boolean; message?: string; answers?: Record<string, unknown> }> => {
  const res = await api(token, '/form', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prepopulate: true, ...params }),
  });

  const json = await res.json();
  const parsed = typeof json?.body === 'string' ? JSON.parse(json.body) : json;
  return {
    matched: !!parsed?.matched,
    message: parsed?.message,
    answers:
      parsed?.answers && typeof parsed.answers === 'object'
        ? (parsed.answers as Record<string, unknown>)
        : undefined,
  };
};

export const queryAthenaLookup = async (
  token: string,
  params: AthenaLookupRequest,
): Promise<AthenaLookupResponse> => {
  const res = await api(token, '/lookup/query', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  const json = await res.json();
  const parsed = typeof json?.body === 'string' ? JSON.parse(json.body) : json;
  const rows = Array.isArray(parsed?.rows) ? parsed.rows : [];

  return { rows };
};

export const updateForm = async (
  token: string,
  params: UpdateFormParams,
  queryParams?: Record<string, string>,
): Promise<void> => {
  // there is no content returned from the update endpoint
  await api(
    token,
    '/form',
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    },
    queryParams,
  );
};

export const deleteForm = async (
  token: string,
  params?: Record<string, string>,
): Promise<boolean> => {
  const res = await api(
    token,
    '/form',
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    },
    params,
  );
  if (res.status === 200) {
    return true;
  }
  throw new ApiError(content.errorApi);
};

export const submitDeclaration = async (
  token: string,
  params?: Record<string, string>,
): Promise<{ submittedAt?: string; formId?: string } | boolean> => {
  const res = await api(
    token,
    '/form/submit',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    },
    params,
  );
  if (res.status === 200) {
    try {
      const json = await res.json();
      const parsed = typeof json?.body === 'string' ? JSON.parse(json.body) : json;
      return {
        submittedAt: parsed?.submitted_at || parsed?.submittedAt,
        formId: parsed?.form_id || parsed?.formId,
      };
    } catch (_err) {
      return true;
    }
  }
  throw new ApiError(content.errorApi); // Can this ever be reached?
};

export const submitFeedback = async (
  token: string,
  body: {
    feedback: string;
    current_url: string;
    form_id?: string;
    share_email: boolean;
  },
): Promise<{ submittedAt?: string }> => {
  const res = await api(token, '/feedback', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const json = await res.json();
  const parsed = typeof json?.body === 'string' ? JSON.parse(json.body) : json;
  return {
    submittedAt: parsed?.submitted_at || parsed?.submittedAt,
  };
};

export const fetchFeedback = async (token: string): Promise<FeedbackEntry[]> => {
  const res = await api(token, '/feedback', {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  const json = await res.json();
  const parsed = typeof json?.body === 'string' ? JSON.parse(json.body) : json;
  const items = Array.isArray(parsed?.feedback)
    ? (parsed.feedback as Array<Record<string, unknown>>)
    : [];
  return items.map((item: Record<string, unknown>) => ({
    tenant: typeof item.tenant === 'string' ? item.tenant : undefined,
    dcfId:
      typeof item.dcf_id === 'string'
        ? item.dcf_id
        : typeof item.dcfId === 'string'
          ? item.dcfId
          : undefined,
    submitterEmail:
      typeof item.submitter_email === 'string'
        ? item.submitter_email
        : typeof item.submitterEmail === 'string'
          ? item.submitterEmail
          : undefined,
    currentUrl:
      typeof item.current_url === 'string'
        ? item.current_url
        : typeof item.currentUrl === 'string'
          ? item.currentUrl
          : undefined,
    formId:
      typeof item.form_id === 'string'
        ? item.form_id
        : typeof item.formId === 'string'
          ? item.formId
          : undefined,
    feedback: typeof item.feedback === 'string' ? item.feedback : undefined,
    submittedAt:
      typeof item.submitted_at === 'string'
        ? item.submitted_at
        : typeof item.submittedAt === 'string'
          ? item.submittedAt
          : undefined,
  }));
};

export const fetchDataErrors = async (
  token: string,
  options?: { includeDeleted?: boolean },
): Promise<DataErrorEntry[]> => {
  const params: Record<string, string> = { record_type: 'data_error' };
  if (options?.includeDeleted) {
    params.include_deleted = 'true';
  }

  const res = await api(
    token,
    '/feedback',
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
    params,
  );

  const json = await res.json();
  const parsed = typeof json?.body === 'string' ? JSON.parse(json.body) : json;
  const items = Array.isArray(parsed?.data_errors)
    ? (parsed.data_errors as Array<Record<string, unknown>>)
    : [];
  return items.map((item: Record<string, unknown>) => {
    if (typeof item.record !== 'string' || !item.record.trim()) {
      throw new Error('Data error item is missing record');
    }

    return {
      raisedBy:
        typeof item.raised_by === 'string'
          ? item.raised_by
          : typeof item.raisedBy === 'string'
            ? item.raisedBy
            : undefined,
      formId:
        typeof item.form_id === 'string'
          ? item.form_id
          : typeof item.formId === 'string'
            ? item.formId
            : undefined,
      record: item.record,
      selected: typeof item.selected === 'string' ? item.selected : undefined,
      notes: typeof item.notes === 'string' ? item.notes : undefined,
      cosNotes:
        typeof item.cos_notes === 'string'
          ? item.cos_notes
          : typeof item.cosNotes === 'string'
            ? item.cosNotes
            : undefined,
      outcome:
        item.outcome === 'accepted' || item.outcome === 'rejected' ? item.outcome : undefined,
      submittedAt:
        typeof item.submitted_at === 'string'
          ? item.submitted_at
          : typeof item.submittedAt === 'string'
            ? item.submittedAt
            : undefined,
      deletedAt:
        typeof item.deleted_at === 'string'
          ? item.deleted_at
          : typeof item.deletedAt === 'string'
            ? item.deletedAt
            : undefined,
    };
  });
};

export const deleteFeedback = async (token: string, submittedAt: string): Promise<void> => {
  await api(
    token,
    '/feedback',
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    },
    { submitted_at: submittedAt },
  );
};

export const deleteDataError = async (token: string, submittedAt: string): Promise<void> => {
  await api(
    token,
    '/feedback',
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    },
    { submitted_at: submittedAt, record_type: 'data_error' },
  );
};

export const downloadExportExcel = async (
  token: string,
  params: Record<string, string>,
): Promise<{ blob: Blob; filename?: string }> => {
  return downloadExportFile(token, { ...params, relational: 'true' }, 'excel');
};

export const unsubmitForm = async (
  token: string,
  body?: Record<string, unknown>,
  params?: Record<string, string>,
): Promise<boolean> => {
  const res = await api(
    token,
    '/form/unsubmit',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
    },
    params,
  );
  if (res.status === 200) {
    return true;
  }
  throw new ApiError(content.errorApi);
};

export const approveForm = async (
  token: string,
  body: Record<string, unknown>,
  params?: Record<string, string>,
): Promise<boolean> => {
  const res = await api(
    token,
    '/form/approve',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
    },
    params,
  );
  if (res.status === 200) {
    return true;
  }
  throw new ApiError(content.errorApi);
};

export const saveScsManagerReview = async (
  token: string,
  body: Record<string, unknown>,
  params?: Record<string, string>,
): Promise<ScsReviewSaveResult> => {
  const res = await api(token, '/form/approve', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...body, managerReview: true }),
  }, params);
  if (res.status !== 200) throw new ApiError(content.errorApi);
  return res.json();
};

export type ScsObjectivesSharePreparation = {
  download_link: string;
  share_token: string;
  filename: string;
};

export type ScsObjectivesEmailResult = {
  email: string;
  sent_at: string;
};

export const prepareScsObjectivesShare = async (
  token: string,
  pdf: string,
  formId: string,
): Promise<ScsObjectivesSharePreparation> => {
  const res = await api(
    token,
    '/form/approve',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'prepare_scs_objectives_share', pdf }),
    },
    { form_id: formId },
  );
  return (await res.json()) as ScsObjectivesSharePreparation;
};

export const sendScsObjectivesEmail = async (
  token: string,
  recipientEmail: string,
  shareToken: string,
  formId: string,
): Promise<ScsObjectivesEmailResult> => {
  const res = await api(
    token,
    '/form/approve',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'send_scs_objectives_email',
        recipient_email: recipientEmail,
        share_token: shareToken,
      }),
    },
    { form_id: formId },
  );
  return (await res.json()) as ScsObjectivesEmailResult;
};

export const fetchForm = async (
  token: string,
  params?: Record<string, string>,
): Promise<FetchFormResponse> => {
  const res = await api(
    token,
    '/form',
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
    params,
  );

  const json = await res.json();

  try {
    if (typeof json === 'string') {
      return JSON.parse(json) as FetchFormResponse;
    }
    if (typeof json?.body === 'string' && json.body && json.body !== 'undefined') {
      return JSON.parse(json.body) as FetchFormResponse;
    }
    return (json || {}) as FetchFormResponse;
  } catch (error) {
    console.error('fetchForm error', error);
    return (json || {}) as FetchFormResponse;
  }
};

export const recordSessionAuditAction = async (
  token: string,
  action: 'login' | 'logout',
  params?: Record<string, string>,
): Promise<boolean> => {
  const res = await api(
    token,
    '/form',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action }),
    },
    params,
  );
  if (res.status === 200) {
    return true;
  }
  throw new ApiError(content.errorApi);
};

export const recordRewardVoucherIssued = async (
  token: string,
  body: {
    formId: string;
    nomineeId: string;
    awardType?: string | number;
    voucherIssued?: boolean;
    voucherBulkUploaded?: boolean;
    voucherBulkUploadStatus?: 'issued' | 'error';
  },
): Promise<boolean> => {
  const res = await api(token, '/form', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'reward_voucher_issued',
      formId: body.formId,
      nomineeId: body.nomineeId,
      awardType: body.awardType,
      voucherIssued: body.voucherIssued,
      voucherBulkUploaded: body.voucherBulkUploaded,
      voucherBulkUploadStatus: body.voucherBulkUploadStatus,
    }),
  });
  if (res.status === 200) {
    return true;
  }
  throw new ApiError(content.errorApi);
};

export const recordRewardQaDataErrorAudit = async (
  token: string,
  body: {
    submittedAt: string;
    record: string;
    selected?: string;
    notes?: string;
    outcome: 'accepted' | 'rejected';
    formId?: string;
  },
): Promise<boolean> => {
  const res = await api(token, '/form', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'reward_qa_data_error_audit',
      submittedAt: body.submittedAt,
      record: body.record,
      selected: body.selected,
      notes: body.notes,
      outcome: body.outcome,
      formId: body.formId,
    }),
  });
  if (res.status === 200) {
    return true;
  }
  throw new ApiError(content.errorApi);
};

export const fetchRewardCosAudit = async (
  token: string,
  body: { formId: string; nomineeId: string; awardType?: string | number; identifier?: string },
): Promise<RewardCosAuditResponse> => {
  const res = await api(token, '/form', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'reward_cos_audit',
      formId: body.formId,
      nomineeId: body.nomineeId,
      awardType: body.awardType,
      identifier: body.identifier,
    }),
  });
  if (res.status !== 200) {
    throw new ApiError(content.errorApi);
  }

  const json = await res.json();
  return (typeof json?.body === 'string' ? JSON.parse(json.body) : json) as RewardCosAuditResponse;
};

export const fetchPsfaAudit = async (
  token: string,
  body: { formId: string; identifier?: string },
): Promise<RewardCosAuditResponse> => {
  const res = await api(token, '/form', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'psfa_audit',
      formId: body.formId,
      identifier: body.identifier,
    }),
  });
  if (res.status !== 200) {
    throw new ApiError(content.errorApi);
  }

  const json = await res.json();
  return (typeof json?.body === 'string' ? JSON.parse(json.body) : json) as RewardCosAuditResponse;
};

export const fetchRewardTransferOwnershipCandidates = async (
  token: string,
): Promise<RewardTransferOwnershipCandidatesResponse> => {
  const res = await api(token, '/form', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'reward_transfer_ownership_list',
    }),
  });
  if (res.status !== 200) {
    throw new ApiError(content.errorApi);
  }

  const json = await res.json();
  const parsed = typeof json?.body === 'string' ? JSON.parse(json.body) : json;
  return {
    candidates: Array.isArray(parsed?.candidates) ? parsed.candidates : [],
  };
};

export const transferRewardOwnership = async (
  token: string,
  body: { formId: string; newFunction: string },
): Promise<RewardTransferOwnershipResponse> => {
  const res = await api(token, '/form', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'reward_transfer_ownership',
      formId: body.formId,
      newFunction: body.newFunction,
    }),
  });
  if (res.status !== 200) {
    throw new ApiError(content.errorApi);
  }

  const json = await res.json();
  return (typeof json?.body === 'string'
    ? JSON.parse(json.body)
    : json) as RewardTransferOwnershipResponse;
};

export type RewardVoucherBulkEntry = {
  formId: string;
  nomineeId: string;
  awardType?: string | number;
};

export const buildRewardCosReconciliationReport = async (
  token: string,
): Promise<{ url: string; filename?: string; count: number }> => {
  const res = await api(token, '/form', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'reward_cos_reconciliation_report',
    }),
  });
  if (res.status !== 200) {
    throw new ApiError(content.errorApi);
  }

  const json = await res.json();
  const parsed = typeof json?.body === 'string' ? JSON.parse(json.body) : json;
  const attachment = parsed?.attachment || {};
  return {
    url: String(attachment.url || ''),
    filename: typeof attachment.filename === 'string' ? attachment.filename : undefined,
    count: Number(parsed?.count || 0),
  };
};

export const buildRewardVoucherBulkUpload = async (
  token: string,
  entries: RewardVoucherBulkEntry[],
): Promise<{ url: string; filename?: string; count: number }> => {
  const res = await api(token, '/form', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'reward_voucher_bulk_download',
      entries,
    }),
  });
  if (res.status !== 200) {
    throw new ApiError(content.errorApi);
  }

  const json = await res.json();
  const parsed = typeof json?.body === 'string' ? JSON.parse(json.body) : json;
  const attachment = parsed?.attachment || {};
  return {
    url: String(attachment.url || ''),
    filename: typeof attachment.filename === 'string' ? attachment.filename : undefined,
    count: Number(parsed?.count || 0),
  };
};

export const recordRewardVoucherBulkUpload = async (
  token: string,
  entries: RewardVoucherBulkEntry[],
): Promise<boolean> => {
  const res = await api(token, '/form', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'reward_voucher_bulk_upload',
      entries,
      bulkUploadComplete: true,
    }),
  });
  if (res.status === 200) {
    return true;
  }
  throw new ApiError(content.errorApi);
};

export const fetchFormUsers = async (
  token: string,
  params: Record<string, string>,
): Promise<FormUsersResponse> => {
  const res = await api(
    token,
    '/form/users',
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
    params,
  );

  const json = await res.json();

  try {
    if (typeof json === 'string') {
      return JSON.parse(json) as FormUsersResponse;
    }
    if (typeof json?.body === 'string' && json.body && json.body !== 'undefined') {
      return JSON.parse(json.body) as FormUsersResponse;
    }
    return (json || {}) as FormUsersResponse;
  } catch (error) {
    console.error('fetchFormUsers error', error);
    return (json || {}) as FormUsersResponse;
  }
};

export const addFormUser = async (
  token: string,
  body: { email: string; role: string; scope?: string; notes?: string },
  params: Record<string, string>,
): Promise<boolean> => {
  const res = await api(
    token,
    '/form/users',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
    params,
  );
  if (res.status === 200) {
    return true;
  }
  throw new ApiError(content.errorApi);
};

export const removeFormUser = async (
  token: string,
  body: { userId: string },
  params: Record<string, string>,
): Promise<boolean> => {
  const res = await api(
    token,
    '/form/users',
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
    params,
  );
  if (res.status === 200) {
    return true;
  }
  throw new ApiError(content.errorApi);
};

export const fetchShareHistory = async (
  token: string,
  params: Record<string, string>,
): Promise<ShareHistoryResponse> => {
  const res = await api(
    token,
    '/form/users',
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
    { ...params, share_history: 'true' },
  );

  const json = await res.json();

  try {
    if (typeof json === 'string') {
      return JSON.parse(json) as ShareHistoryResponse;
    }
    if (typeof json?.body === 'string' && json.body && json.body !== 'undefined') {
      return JSON.parse(json.body) as ShareHistoryResponse;
    }
    return (json || { history: [] }) as ShareHistoryResponse;
  } catch (error) {
    console.error('fetchShareHistory error', error);
    return (json || { history: [] }) as ShareHistoryResponse;
  }
};

const parseFunctionMembersResponse = async (res: Response): Promise<FunctionMembersResponse> => {
  const json = await res.json();
  try {
    if (typeof json === 'string') {
      return JSON.parse(json) as FunctionMembersResponse;
    }
    if (typeof json?.body === 'string' && json.body && json.body !== 'undefined') {
      return JSON.parse(json.body) as FunctionMembersResponse;
    }
    return (json || { functions: [], members: [] }) as FunctionMembersResponse;
  } catch (error) {
    console.error('function members parse error', error);
    return (json || { functions: [], members: [] }) as FunctionMembersResponse;
  }
};

export const fetchFunctionMembers = async (
  token: string,
  functionName?: string,
): Promise<FunctionMembersResponse> => {
  return parseFunctionMembersResponse(
    await api(
      token,
      '/form/users',
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      },
      {
        function_members: 'true',
        ...(functionName ? { functionName } : {}),
      },
    ),
  );
};

export const addFunctionMember = async (
  token: string,
  body: { email: string; functionName: string },
): Promise<boolean> => {
  const res = await api(
    token,
    '/form/users',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
    { function_members: 'true' },
  );
  return res.status === 200;
};

export const removeFunctionMember = async (
  token: string,
  body: { username: string; functionName: string },
): Promise<boolean> => {
  const res = await api(
    token,
    '/form/users',
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
    { function_members: 'true' },
  );
  return res.status === 200;
};

export const validateEmailDomain = async (token: string, email: string): Promise<void> => {
  await api(token, '/email/validate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
};

export type RegisterAccountResult = {
  created: boolean;
  exists: boolean;
  status: string;
  email?: string;
};

export const checkRegisterEmail = async (
  email: string,
  tenant?: string,
): Promise<{ exists: boolean; passwordDeferred: boolean }> => {
  authDebug('register.api.check.start', {
    path: '/register/check',
    tenant,
    email,
  });

  try {
    const res = await apiPublic('/register/check', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(tenant ? { 'X-Tenant': tenant } : {}),
      },
      body: JSON.stringify({ email }),
    });

    const body = (await res.json()) as {
      exists?: boolean;
      passwordDeferred?: boolean;
      valid?: boolean;
      errorMessage?: string;
    };

    authDebug('register.api.check.response', {
      path: '/register/check',
      tenant,
      email,
      status: res.status,
      exists: body.exists,
      valid: body.valid,
      hasErrorMessage: Boolean(body.errorMessage),
    });

    if (body.valid === false) {
      throw new Error(
        typeof body.errorMessage === 'string' && body.errorMessage.trim()
          ? body.errorMessage
          : content.errorApi,
      );
    }

    return { exists: body.exists === true, passwordDeferred: body.passwordDeferred === true };
  } catch (error) {
    authDebug('register.api.check.error', {
      path: '/register/check',
      tenant,
      email,
      error: getAuthDebugError(error),
    });
    throw error;
  }
};

export const createRegisterAccount = async (
  email: string,
  tenant?: string,
): Promise<RegisterAccountResult> => {
  authDebug('register.api.create.start', {
    path: '/register/create',
    tenant,
    email,
  });

  try {
    const res = await apiPublic('/register/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(tenant ? { 'X-Tenant': tenant } : {}),
      },
      body: JSON.stringify({ email }),
    });

    const body = (await res.json()) as Partial<RegisterAccountResult>;
    const result = {
      created: body.created === true,
      exists: body.exists === true,
      status: typeof body.status === 'string' ? body.status : 'unknown',
      ...(typeof body.email === 'string' ? { email: body.email } : {}),
    };
    authDebug('register.api.create.response', {
      path: '/register/create',
      tenant,
      email,
      status: res.status,
      resultStatus: result.status,
      created: result.created,
      exists: result.exists,
    });
    return result;
  } catch (error) {
    authDebug('register.api.create.error', {
      path: '/register/create',
      tenant,
      email,
      error: getAuthDebugError(error),
    });
    throw error;
  }
};

export const triggerRegisterPasswordReset = async (
  email: string,
  tenant?: string,
): Promise<RegisterAccountResult> => {
  authDebug('register.api.reset.start', {
    path: '/register/create',
    tenant,
    email,
  });

  try {
    const res = await apiPublic('/register/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(tenant ? { 'X-Tenant': tenant } : {}),
      },
      body: JSON.stringify({ email, action: 'register_reset' }),
    });

    const body = (await res.json()) as Partial<RegisterAccountResult>;
    const result = {
      created: body.created === true,
      exists: body.exists === true,
      status: typeof body.status === 'string' ? body.status : 'unknown',
      ...(typeof body.email === 'string' ? { email: body.email } : {}),
    };
    authDebug('register.api.reset.response', {
      path: '/register/create',
      tenant,
      email,
      status: res.status,
      resultStatus: result.status,
      created: result.created,
      exists: result.exists,
    });
    return result;
  } catch (error) {
    authDebug('register.api.reset.error', {
      path: '/register/create',
      tenant,
      email,
      error: getAuthDebugError(error),
    });
    throw error;
  }
};

export const prepareStaffSignIn = async (
  email: string,
  tenant?: string,
  expired = false,
): Promise<boolean> => {
  const response = await apiPublic('/register/create', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(tenant ? { 'X-Tenant': tenant } : {}),
    },
    body: JSON.stringify({ email, action: expired ? 'signin_expired' : 'signin_start' }),
  });
  const body = await response.json();
  return body.status === 'password_sent';
};
